import React, { useEffect, useState } from 'react';
import { XIcon, SparklesIcon } from 'lucide-react';
const MOOD_EMOJIS = ['😊', '🥳', '💪', '😌', '🌟'];
const TONE_OPTIONS = ['Gentle', 'Balanced', 'Intense'];
const MOOD_SUGGESTIONS = ['Energized and motivated', 'Calm and peaceful', 'Confident and powerful', 'Happy and uplifted', 'Focused and productive'];
interface MoodMixModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateMix: (mood: {
    currentMood: string;
    desiredMood: string;
    tone: string;
  }) => void;
}
export const MoodMixModal = ({
  isOpen,
  onClose,
  onCreateMix
}: MoodMixModalProps) => {
  const [currentMood, setCurrentMood] = useState('');
  const [desiredMood, setDesiredMood] = useState('');
  const [selectedEmoji, setSelectedEmoji] = useState('');
  const [selectedTone, setSelectedTone] = useState('Balanced');
  const [step, setStep] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // Check if device is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);
  if (!isOpen) return null;
  const getGradientByMood = (mood: string) => {
    if (mood.includes('energized') || mood.includes('motivated')) {
      return 'from-yellow-400 via-orange-300 to-red-400';
    }
    if (mood.includes('calm') || mood.includes('peaceful')) {
      return 'from-blue-400 via-indigo-500 to-purple-600';
    }
    if (mood.includes('confident') || mood.includes('powerful')) {
      return 'from-purple-500 via-pink-500 to-red-500';
    }
    return 'from-purple-600 via-blue-500 to-green-400';
  };
  const handleCreate = () => {
    onCreateMix({
      currentMood,
      desiredMood,
      tone: selectedTone
    });
  };
  const isValid = desiredMood.trim() !== '';
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75" data-id="element-243">
      <div className={`
          bg-[#282828] w-full max-w-lg mx-4 relative overflow-hidden
          ${isMobile ? 'rounded-t-xl h-[80vh] mt-auto mb-0' : 'rounded-xl my-4'}
          transition-all duration-500
          bg-gradient-to-br ${getGradientByMood(desiredMood)}
        `} data-id="element-244">
        <div className="absolute inset-0 bg-black bg-opacity-75 backdrop-blur-sm" data-id="element-245" />
        <div className="relative z-10 p-6 sm:p-8" data-id="element-246">
          <button onClick={onClose} className="absolute right-4 top-4 p-2 hover:bg-white/10 rounded-full transition-colors" data-id="element-247">
            <XIcon className="w-6 h-6" data-id="element-248" />
          </button>
          {step === 1 ? <>
              <h2 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-center" data-id="element-249">
                How do you want to feel?
              </h2>
              <div className="space-y-4 sm:space-y-6" data-id="element-250">
                <div className="relative" data-id="element-251">
                  <input type="text" value={desiredMood} onChange={e => setDesiredMood(e.target.value)} placeholder="e.g. Energized and motivated" className="w-full bg-black bg-opacity-50 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:outline-none text-base" data-id="element-252" />
                  <SparklesIcon className="absolute right-3 top-3 w-5 h-5 text-gray-400" data-id="element-253" />
                </div>
                <div className="grid grid-cols-2 gap-2" data-id="element-254">
                  {MOOD_SUGGESTIONS.map(mood => <button key={mood} onClick={() => setDesiredMood(mood)} className={`
                        px-4 py-3 sm:py-2 rounded-lg text-sm transition-all text-left
                        ${desiredMood === mood ? 'bg-white text-black' : 'bg-white/10 hover:bg-white/20'}
                      `} data-id="element-255">
                      {mood}
                    </button>)}
                </div>
                <div className="flex flex-col sm:flex-row justify-center space-y-2 sm:space-y-0 sm:space-x-2" data-id="element-256">
                  {TONE_OPTIONS.map(tone => <button key={tone} onClick={() => setSelectedTone(tone)} className={`
                        px-6 py-3 sm:py-2 rounded-full transition-all w-full sm:w-auto
                        ${selectedTone === tone ? 'bg-white text-black' : 'border border-white border-opacity-50 hover:border-opacity-100'}
                      `} data-id="element-257">
                      {tone}
                    </button>)}
                </div>
                <button onClick={handleCreate} disabled={!isValid} className={`
                    w-full py-4 rounded-full font-bold text-lg transition-all mt-4
                    ${isValid ? 'bg-green-500 hover:bg-green-400 text-black' : 'bg-gray-700 text-gray-400 cursor-not-allowed'}
                  `} data-id="element-258">
                  Create Mood Journey
                </button>
              </div>
            </> : null}
        </div>
      </div>
    </div>;
};